/* eslint-disable */
import { CylinderGeometry, MeshStandardMaterial, Vector3 } from 'three';

import * as Helpers from '../helpers';
import { Object3D } from '../Object3D';

export class MinimalistDoor extends Object3D {
  constructor(...args) {
    super(...args);

    this.materials = {
      handle: new MeshStandardMaterial(),
      metal: new MeshStandardMaterial(),
      wood: new MeshStandardMaterial(),
    };

    this.parameters.hangerMargin = 100;
    this.parameters.sideBarWidth = 40;
    this.parameters.doorThickness = 30;
  }

  calculateModelParameters() {
    this.parameters.panelHeights = Helpers.DecomposeMinimalistHeight(
      this.parameters.height
    );
    this.parameters.sideBarHeights = [
      this.parameters.panelHeights[0] + this.parameters.panelHeights[1] / 2,
      this.parameters.panelHeights[1] / 2 +
        this.parameters.panelHeights[2] +
        this.parameters.panelHeights[3] / 2,
      this.parameters.panelHeights[3] / 2 + this.parameters.panelHeights[4],
    ];
  }

  buildSideBarGeometry() {
    const sideBars = [];
    let sideHeight = 0;
    for (const sideBarHeight of this.parameters.sideBarHeights) {
      const sideBarLeft = Helpers.CreateHalfRoundedRectangle(
        0,
        0,
        this.parameters.sideBarWidth,
        this.parameters.doorThickness,
        10,
        sideBarHeight
      );
      const sideBarRight = Helpers.MirrorObjectX(sideBarLeft, true);
      sideBarLeft.translate(
        this.parameters.sideBarWidth / 2,
        0,
        sideHeight + sideBarHeight / 2
      );
      sideBarRight.translate(
        this.parameters.width - this.parameters.sideBarWidth / 2,
        0,
        sideHeight + sideBarHeight / 2
      );
      sideBars.push(sideBarLeft);
      sideBars.push(sideBarRight);
      sideHeight += sideBarHeight;
    }

    return sideBars;
  }

  buildBodyGeometry() {
    const panels = [];
    let sumHeight = 0;
    for (let i = 0; i < this.parameters.panelHeights.length; i++) {
      const thickness = this.parameters.doorThickness;
      const width = this.parameters.width - 2 * this.parameters.sideBarWidth;
      const height = this.parameters.panelHeights[i];
      let handleMargin;
      let panel = Helpers.Box2Points(
        new Vector3(0, -thickness / 2, 0),
        new Vector3(width, +thickness / 2, height)
      );

      if (i === 2) {
        let handleHole;
        if (this.parameters.style === 'halfmoon') {
          handleHole = Helpers.CreateHalfMoon(0, 0, 130, 357, 2 * thickness);
        } else {
          // handleHole = Helpers.CreateRoundedRectangle(0,0,200 + 60,200 + 60 ,100 + 30,2 * thickness,true);
          handleHole = new CylinderGeometry(130, 130, 2 * thickness, 32);
          Helpers.RotateObject(handleHole, null, 90, 'X');
        }
        handleMargin = this.parameters.style === 'halfmoon' ? 95 : 150;

        Helpers.RotateObject(handleHole, null, 90, 'X');
        handleHole.translate(handleMargin, 0, height / 2);

        panel = Helpers.BooleanDifference(panel, handleHole);
      }

      panel.translate(this.parameters.sideBarWidth, 0, sumHeight);
      panels.push(panel);

      sumHeight += this.parameters.panelHeights[i];
    }

    return panels;
  }

  buildHandleGeometry() {
    const geometries = [];
    let handleMargin;
    const handleOffset = 19;

    const hangerObj =
      this.parameters.style === 'halfmoon'
        ? Helpers.CreateHalfMoon(0, 0, 80, 280, 24)
        : Helpers.CreateRoundedRectangle(0, 0, 200, 200, 100, 24, false);

    if (!hangerObj) {
      return [];
    }

    const geom = hangerObj.clone();
    Helpers.RotateObject(geom, null, 90, 'X');
    handleMargin =
      this.parameters.style === 'halfmoon'
        ? 95 + this.parameters.sideBarWidth
        : 150 + this.parameters.sideBarWidth;

    geom.translate(handleMargin, -handleOffset, this.parameters.height / 2);
    const geomClone = Helpers.MirrorObjectY(geom, true);

    geometries.push(geom);
    geometries.push(geomClone);

    hangerObj.dispose(); // Clean geometries to avoid memory leak

    return geometries;
  }

  buildPlateGeometry() {
    const geometries = [];
    let handleMargin;
    const handleOffset = 7;

    const handleObj =
      this.parameters.style === 'halfmoon'
        ? Helpers.CreateHalfMoon(0, 0, 130, 357, 1)
        : Helpers.CreateRoundedRectangle(
            0,
            0,
            200 + 60,
            200 + 60,
            100 + 30,
            1,
            false
          );

    if (!handleObj) {
      return [];
    }

    const geom = handleObj.clone();
    Helpers.RotateObject(geom, null, 90, 'X');
    handleMargin =
      this.parameters.style === 'halfmoon'
        ? 95 + this.parameters.sideBarWidth
        : 150 + this.parameters.sideBarWidth;

    geom.translate(handleMargin, -handleOffset, this.parameters.height / 2);
    const geomClone = Helpers.MirrorObjectY(geom, true);

    geometries.push(geom);
    geometries.push(geomClone);

    handleObj.dispose(); // Clean geometries to avoid memory leak

    return geometries;
  }

  buildHangerGeometry() {
    const geometries = [];
    const hangerOffset = 20;
    const hangerDimension = 120;
    const heightOffset = 100;
    const hangerObj = this.resourceManager.getObject('hiddenSystem');
    if (!hangerObj) {
      return [];
    }

    hangerObj.children.forEach((z) => {
      const geom = z.geometry.clone();
      Helpers.RotateObject(geom, null, 90, 'X');

      geom.translate(
        hangerDimension + this.parameters.hangerMargin,
        hangerOffset,
        this.parameters.height - heightOffset
      );
      const geom2 = geom.clone();
      geom2.translate(
        this.parameters.width -
          this.parameters.hangerMargin -
          2 * hangerDimension,
        0,
        0
      );

      geometries.push(geom);
      geometries.push(geom2);
    });

    hangerObj.traverse((node) => {
      this.disposeObject(node);
    }); // Clean geometries to avoid memory leak

    return geometries;
  }
}
